/**
 * MasteryPanel — "Padronanza" section (design spec: analytics-design-spec.md).
 *
 * One job: "dimmi cosa ripassare, adesso". Weak-first chapter list, a single
 * bold "Spettro di padronanza" signature strip, everything else quiet.
 *
 * Pure presentational component: the host (Reader.tsx on web) owns the
 * fetch + `computeMasteryReport` call and passes the resulting `MasteryReport`
 * down, plus the expanded-chapter id (URL-synced) and the "Ripassa" navigation
 * callback (URL-synced deep link to `?vista=lettura&sez=..&punto=..`).
 */

import * as React from 'react'
import { clsx } from 'clsx'
import type { MasteryReport } from '@arianna/reader-core'
import { fmtDate } from '@arianna/reader-core'
import Heading from '@arianna/design-system/Heading'
import Text from '@arianna/design-system/Text'
import Stat from '@arianna/design-system/Stat'
import Button from '@arianna/design-system/Button'
import { MasterySpectrum } from './MasterySpectrum.js'
import { ChapterMasteryRow } from './ChapterMasteryRow.js'

export interface MasteryPanelProps {
  /** Only used in the top bar's breadcrumb — irrelevant when `showHeader` is false. */
  bundleTitle?: string
  /** null while `loading` or before the analytics fetch has resolved once. */
  report: MasteryReport | null
  loading: boolean
  error: string | null
  /** Currently expanded chapter id (Padronanza `cap` URL param), or null. */
  expandedChapterId: string | null
  onToggleChapter: (chapterId: string) => void
  /** "Ripassa" — navigate to the reading view at this sub-paragraph. */
  onReview: (subParaId: string) => void
  /** "Allenati sui punti deboli" — start a fragile-mode practice session with `count` questions. */
  onStartWeakPractice: (count: number) => void
  /** "Mettiti alla prova" — start a chapter-focused practice session. */
  onStartChapterPractice: (chapterId: string) => void
  /** Only used by the top bar's back-link — irrelevant when `showHeader` is false. */
  onBack?: () => void
  /**
   * Whether to render the top bar (back-link + "Padronanza · {bundleTitle}"
   * breadcrumb) at all. Default true (apps/reader). apps/platform (TASK-014
   * course shell) sets this to false: the persistent CourseLayout
   * header/tabs already provide both the back-navigation (the "Manuale"
   * tab) and the manual title.
   */
  showHeader?: boolean
  className?: string
}

// ---------------------------------------------------------------------------
// Weak-practice question-count selector — mirrors the numQ pill pattern from
// TestsHub (same base options + "total" option), scaled down for this
// secondary CTA. Kept local to this component: the count only matters at the
// moment "Allenati" is pressed, no need to lift it into Reader.tsx state.
// ---------------------------------------------------------------------------

const WEAK_PRACTICE_BASE_OPTIONS = [10, 15, 20, 25] as const
const WEAK_PRACTICE_DEFAULT_COUNT = 15

function weakPracticeOptions(totalQuestions: number): number[] {
  const opts: number[] = WEAK_PRACTICE_BASE_OPTIONS.filter((n) => n < totalQuestions)
  return opts.includes(totalQuestions) ? opts : [...opts, totalQuestions]
}

function chapterAnchorId(chapterId: string): string {
  return `mastery-chapter-${chapterId}`
}

function scrollToChapter(chapterId: string) {
  const el = document.getElementById(chapterAnchorId(chapterId))
  if (el == null) return
  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' })
}

function overallColorClass(pct: number): 'green' | 'gold' | 'red' {
  if (pct >= 0.8) return 'green'
  if (pct >= 0.5) return 'gold'
  return 'red'
}

function unassignedNoteCopy(answered: number): string {
  const risposta = answered === 1 ? 'risposta' : 'risposte'
  const attribuita = answered === 1 ? 'attribuita' : 'attribuite'
  return `${answered} ${risposta} non ancora ${attribuita} a un capitolo — rigenera il manuale ed esegui il backfill.`
}

export function MasteryPanel({
  bundleTitle,
  report,
  loading,
  error,
  expandedChapterId,
  onToggleChapter,
  onReview,
  onStartWeakPractice,
  onStartChapterPractice,
  onBack,
  showHeader = true,
  className,
}: MasteryPanelProps) {
  const handleSegmentClick = React.useCallback((chapterId: string) => {
    scrollToChapter(chapterId)
  }, [])

  const hasUnassignedNote = (report?.unassigned?.answered ?? 0) > 0

  const needsReviewCount = React.useMemo(
    () =>
      report?.chapters.filter((c) => c.state === 'weak' || c.state === 'consolidate').length ?? 0,
    [report],
  )

  const [weakPracticeCount, setWeakPracticeCount] = React.useState(WEAK_PRACTICE_DEFAULT_COUNT)
  const practiceOptions = React.useMemo(
    () => weakPracticeOptions(report?.totals.questionsTotal ?? 0),
    [report],
  )
  const selectedPracticeCount = report
    ? Math.min(weakPracticeCount, report.totals.questionsTotal)
    : weakPracticeCount

  return (
    <div className={clsx('flex flex-col h-full bg-bg font-sans overflow-hidden', className)}>
      {/* Top bar — mirrors QuizResults for a consistent overlay chrome.
          TASK-014 dedup: hidden on apps/platform (showHeader={false}) — the
          course shell's own header/tabs already cover both the back-link
          and the manual title breadcrumb. */}
      {showHeader && (
        <div className="flex items-center px-8 py-[14px] border-b border-line shrink-0">
          <button
            onClick={onBack}
            className="bg-transparent border-none text-ink-3 text-sm cursor-pointer font-sans flex items-center gap-[6px] p-0 shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 rounded"
          >
            <span>←</span>
            Lettura
          </button>
          <div className="flex-1 text-center text-sm text-ink-3 overflow-hidden text-ellipsis whitespace-nowrap">
            Padronanza · {bundleTitle}
          </div>
          <div className="w-[70px] shrink-0" />
        </div>
      )}

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[820px] mx-auto px-12 pt-11 pb-[80px]">
          {error != null && (
            <div className="mb-6 px-4 py-3 rounded-md border border-red bg-surface text-sm text-red">
              {error}
            </div>
          )}

          {loading && report == null && error == null && (
            <Text variant="body" className="text-ink-3">
              Caricamento padronanza…
            </Text>
          )}

          {report != null && (
            <>
              {/* sectionCompact as h1 — the shared top-level heading variant
                  of every course section (see ReaderDocument/TestsHub/Overview). */}
              <Heading variant="sectionCompact" as="h1" className="mb-1">
                Padronanza
              </Heading>
              <Text
                variant="meta"
                className={clsx('text-ink-3 block', hasUnassignedNote ? 'mb-1' : 'mb-5')}
              >
                Basato su {report.totals.answered} risposte · aggiornato{' '}
                {fmtDate(new Date(report.generatedAt).getTime())}
              </Text>

              {hasUnassignedNote && (
                <Text variant="meta" className="text-ink-3 italic mb-5 block">
                  {unassignedNoteCopy(report.unassigned!.answered)}
                </Text>
              )}

              {report.totals.answered === 0 ? (
                <div className="mt-4 px-5 py-6 rounded-lg border border-dashed border-line-2 bg-surface text-[15px] text-ink-2">
                  Nessun quiz ancora. Fanne uno e qui comparirà la tua mappa di padronanza.
                </div>
              ) : (
                <>
                  <div className="flex gap-3 flex-wrap mb-7">
                    <Stat
                      label="padronanza media"
                      n={`${Math.round(report.totals.masteryPct * 100)}%`}
                      color={overallColorClass(report.totals.masteryPct)}
                    />
                    <Stat
                      label={
                        needsReviewCount === 1 ? 'capitolo da ripassare' : 'capitoli da ripassare'
                      }
                      n={needsReviewCount}
                      color={needsReviewCount > 0 ? 'gold' : 'green'}
                    />
                  </div>

                  {/* Global weak-practice CTA — "in cima alla Padronanza" per spec */}
                  <div className="mb-8 px-4 py-4 rounded-lg border border-line bg-surface flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-[10px] text-ink-3 tracking-[0.12em] uppercase font-semibold mb-2">
                        Numero di domande
                      </div>
                      <div className="flex gap-[6px] flex-wrap">
                        {practiceOptions.map((n) => {
                          const active = selectedPracticeCount === n
                          const label =
                            n === report.totals.questionsTotal ? `${n} · tutte` : String(n)
                          return (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setWeakPracticeCount(n)}
                              aria-pressed={active}
                              className={clsx(
                                'py-1 px-3 text-xs rounded-full cursor-pointer font-sans transition-colors duration-150',
                                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
                                active
                                  ? 'font-semibold bg-ink text-bg border border-ink'
                                  : 'font-medium bg-transparent text-ink-3 border border-line',
                              )}
                            >
                              {label}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="accent"
                      size="compact"
                      onClick={() => onStartWeakPractice(selectedPracticeCount)}
                      className="shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
                    >
                      Allenati sui punti deboli
                    </Button>
                  </div>

                  <MasterySpectrum
                    chapters={report.chapters}
                    onSegmentClick={handleSegmentClick}
                    className="mb-8"
                  />

                  <div className="flex flex-col gap-3">
                    {report.chapters.map((chapter) => (
                      <ChapterMasteryRow
                        key={chapter.chapterId}
                        chapter={chapter}
                        anchorId={chapterAnchorId(chapter.chapterId)}
                        expanded={expandedChapterId === chapter.chapterId}
                        onToggle={onToggleChapter}
                        onReview={onReview}
                        onStartChapterPractice={onStartChapterPractice}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
